import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import EarlyAccessPilotBadge from '@/components/environment/EarlyAccessPilotBadge';
import { EARLY_ACCESS_PILOT_MESSAGE, isEarlyAccessPilotEnvironment } from '@/lib/earlyAccessPilot';
import LandingPreview from './LandingPreview';

export type LandingMode = 'default' | 'maintenance';

type LandingPageProps = {
  mode?: LandingMode;
  maintenanceMessage?: string;
};

type LegalLink = {
  to: string;
  label: string;
};

const legalLinks: LegalLink[] = [
  {
    to: '/data-deletion',
    label: 'Data Deletion',
  },
  {
    to: '/privacy-policy',
    label: 'Privacy Policy',
  },
  {
    to: '/terms-of-service',
    label: 'Terms of Service',
  },
];

type LandingInfoCard = {
  title: string;
  body: string;
};

const infoCards: LandingInfoCard[] = [
  {
    title: 'About Play Claw',
    body: 'Play Claw is a live online claw game by DwellBot Games. Players watch a real robot camera feed, join the queue, and take turns controlling the claw to try to grab prizes.',
  },
  {
    title: 'Why sign in',
    body: 'Sign-in is used to create and secure player accounts, let players join the queue, manage gameplay sessions, and access account-related features.',
  },
];

const LandingPage: React.FC<LandingPageProps> = ({
  mode = 'default',
  maintenanceMessage = 'Under Maintenance: No Claw Robots Available Right Now',
}) => {
  const navigate = useNavigate();
  const isMaintenance = mode === 'maintenance';
  const shouldShowEarlyAccessPilot = isEarlyAccessPilotEnvironment(import.meta.env.MODE);

  return (
    <div className="relative min-h-screen min-h-[100svh] w-full overflow-x-hidden bg-[#040712] text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(248,196,113,0.18),transparent_38%),linear-gradient(180deg,rgba(8,13,28,0.94),rgba(3,6,16,1))]" />
      <div className="absolute left-1/2 top-[-7rem] h-[22rem] w-[22rem] -translate-x-1/2 rounded-full bg-[#f59e0b]/18 blur-3xl" />
      <div className="absolute bottom-[-8rem] left-[-5rem] h-[18rem] w-[18rem] rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="absolute right-[-4rem] top-1/3 h-[16rem] w-[16rem] rounded-full bg-fuchsia-500/10 blur-3xl" />
      <div className="relative z-10 mx-auto flex min-h-screen min-h-[100svh] w-full max-w-3xl flex-col items-center justify-center gap-6 p-6 text-center sm:p-8">
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Badge className="border-white/20 bg-white/10 text-white">DwellBot Games</Badge>
          {shouldShowEarlyAccessPilot ? <EarlyAccessPilotBadge /> : null}
        </div>
        <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
          Play Claw by DwellBot Games
        </h1>
        <p className="max-w-2xl text-base text-muted-foreground sm:text-lg">
          Watch the live robot floor feed, join the queue, and take turns controlling a real claw machine to grab
          prizes.
        </p>
        <LandingPreview />
        {isMaintenance ? (
          <div className="rounded-2xl border border-white/15 bg-black/40 px-6 py-4 text-base text-white">
            {maintenanceMessage}
          </div>
        ) : (
          <Button size="lg" className="px-8" onClick={() => navigate('/sign-in')}>
            Play Now
          </Button>
        )}
        {shouldShowEarlyAccessPilot ? (
          <p className="max-w-2xl text-sm leading-6 text-amber-100/85 sm:text-base">
            {EARLY_ACCESS_PILOT_MESSAGE}
          </p>
        ) : null}
        <section
          aria-label="About Play Claw"
          className="grid w-full gap-3 rounded-[2rem] border border-white/12 bg-black/35 p-4 text-left shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-md sm:p-5"
        >
          {infoCards.map((card) => (
            <div key={card.title} className="rounded-[1.5rem] border border-white/10 bg-white/5 p-4 sm:p-5">
              <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-white/75">{card.title}</h2>
              <p className="mt-2 text-sm leading-6 text-white/80 sm:text-[0.95rem]">{card.body}</p>
            </div>
          ))}
          <div className="rounded-[1.5rem] border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/75 sm:px-5">
            Operated by <span className="font-semibold text-white">DwellBot Games</span>. Support:{' '}
            <a
              className="text-white underline decoration-white/30 underline-offset-4 transition hover:decoration-white/70"
              href="mailto:support@dwell.bot"
            >
              support@dwell.bot
            </a>
          </div>
        </section>
        <div className="rounded-full border border-white/12 bg-black/30 px-4 py-2 backdrop-blur-sm">
          <nav aria-label="Legal" className="flex flex-wrap items-center justify-center gap-2 text-xs sm:text-sm">
            {legalLinks.map((link, index) => (
              <React.Fragment key={link.to}>
                {index > 0 ? <span className="text-white/30">•</span> : null}
                <Link
                  className="text-white/70 underline decoration-white/25 underline-offset-4 transition hover:text-white hover:decoration-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
                  to={link.to}
                >
                  {link.label}
                </Link>
              </React.Fragment>
            ))}
            <span className="text-white/30">•</span>
            <a
              className="text-white/70 underline decoration-white/25 underline-offset-4 transition hover:text-white hover:decoration-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
              href="mailto:support@dwell.bot"
            >
              Support
            </a>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
